import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Badge } from '@os/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@os/components/ui/table';
import { BarChart3, TrendingUp, Target, Calculator } from 'lucide-react';
// @ts-ignore
import jStat from 'jstat';
import * as ss from 'simple-statistics';

interface DescriptiveStatsProps {
  data: number[];
}

export const DescriptiveStats: React.FC<DescriptiveStatsProps> = ({ data }) => {
  const stats = useMemo(() => {
    if (data.length === 0) return null;

    const sortedData = [...data].sort((a, b) => a - b);
    
    return {
      // Basic measures
      count: data.length,
      sum: ss.sum(data),
      mean: ss.mean(data),
      median: ss.median(data),
      mode: ss.mode(data),
      
      // Variability measures
      variance: ss.variance(data),
      standardDeviation: ss.standardDeviation(data),
      standardError: ss.standardDeviation(data) / Math.sqrt(data.length),
      range: Math.max(...data) - Math.min(...data),
      
      // Position measures
      minimum: Math.min(...data),
      maximum: Math.max(...data),
      q1: ss.quantile(data, 0.25),
      q3: ss.quantile(data, 0.75),
      iqr: ss.quantile(data, 0.75) - ss.quantile(data, 0.25),
      
      // Shape measures
      skewness: ss.sampleSkewness(data),
      kurtosis: ss.sampleKurtosis(data),
      
      // Confidence intervals (95%)
      confidenceInterval: {
        lower: ss.mean(data) - 1.96 * (ss.standardDeviation(data) / Math.sqrt(data.length)),
        upper: ss.mean(data) + 1.96 * (ss.standardDeviation(data) / Math.sqrt(data.length))
      }
    };
  }, [data]);

  if (!stats) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Calculator className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Data Available</h3>
          <p className="text-muted-foreground">Enter numerical data in the sidebar to begin analysis.</p>
        </div>
      </div>
    );
  }

  const centralTendency = [
    { measure: 'Count', value: stats.count.toString(), description: 'Number of observations' },
    { measure: 'Sum', value: stats.sum.toFixed(4), description: 'Total of all values' },
    { measure: 'Mean', value: stats.mean.toFixed(4), description: 'Arithmetic average' },
    { measure: 'Median', value: stats.median.toFixed(4), description: 'Middle value' },
    { measure: 'Mode', value: Array.isArray(stats.mode) ? stats.mode.join(', ') : stats.mode?.toFixed(4) || 'N/A', description: 'Most frequent value(s)' }
  ];

  const variability = [
    { measure: 'Range', value: stats.range.toFixed(4), description: 'Max - Min' },
    { measure: 'Variance', value: stats.variance.toFixed(4), description: 'Average squared deviation' },
    { measure: 'Standard Deviation', value: stats.standardDeviation.toFixed(4), description: 'Square root of variance' },
    { measure: 'Standard Error', value: stats.standardError.toFixed(4), description: 'Standard deviation of sample mean' },
    { measure: 'IQR', value: stats.iqr.toFixed(4), description: 'Interquartile range (Q3-Q1)' }
  ];

  const position = [
    { measure: 'Minimum', value: stats.minimum.toFixed(4), description: 'Smallest value' },
    { measure: 'Q1 (25th percentile)', value: stats.q1.toFixed(4), description: 'First quartile' },
    { measure: 'Q3 (75th percentile)', value: stats.q3.toFixed(4), description: 'Third quartile' },
    { measure: 'Maximum', value: stats.maximum.toFixed(4), description: 'Largest value' }
  ];

  const shape = [
    { measure: 'Skewness', value: stats.skewness.toFixed(4), description: 'Measure of asymmetry' },
    { measure: 'Kurtosis', value: stats.kurtosis.toFixed(4), description: 'Measure of tail heaviness' }
  ];

  const getSkewnessInterpretation = (skewness: number) => {
    if (Math.abs(skewness) < 0.5) return 'Approximately symmetric';
    if (skewness > 0.5) return 'Right-skewed (positive)';
    return 'Left-skewed (negative)';
  };

  const getKurtosisInterpretation = (kurtosis: number) => {
    if (kurtosis > 3) return 'Leptokurtic (heavy tails)';
    if (kurtosis < 3) return 'Platykurtic (light tails)';
    return 'Mesokurtic (normal-like)';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-500 text-white rounded-lg">
          <BarChart3 size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Descriptive Statistics</h2>
          <p className="text-muted-foreground">Comprehensive summary of your dataset</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calculator size={16} />
              Sample Size
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.count}</div>
            <p className="text-xs text-muted-foreground">observations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target size={16} />
              Mean
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.mean.toFixed(3)}</div>
            <p className="text-xs text-muted-foreground">± {stats.standardError.toFixed(3)} SE</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp size={16} />
              Std. Deviation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.standardDeviation.toFixed(3)}</div>
            <p className="text-xs text-muted-foreground">variability measure</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">95% CI for Mean</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-mono text-foreground">
              [{stats.confidenceInterval.lower.toFixed(3)}, {stats.confidenceInterval.upper.toFixed(3)}]
            </div>
            <p className="text-xs text-muted-foreground">confidence interval</p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Central Tendency */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Central Tendency</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Measure</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {centralTendency.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{item.measure}</div>
                        <div className="text-xs text-muted-foreground">{item.description}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{item.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Variability */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Variability</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Measure</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {variability.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{item.measure}</div>
                        <div className="text-xs text-muted-foreground">{item.description}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{item.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Position Measures */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Position Measures</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Measure</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {position.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{item.measure}</div>
                        <div className="text-xs text-muted-foreground">{item.description}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{item.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Distribution Shape */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Distribution Shape</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Measure</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shape.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{item.measure}</div>
                        <div className="text-xs text-muted-foreground">{item.description}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-mono">{item.value}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {item.measure === 'Skewness' && getSkewnessInterpretation(stats.skewness)}
                        {item.measure === 'Kurtosis' && getKurtosisInterpretation(stats.kurtosis)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};